import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, KeyRound, UserPlus, Shield, ShieldOff, Trash2, Check, X, Loader2, RefreshCw, Mail, Lock, Fuel, Bell, BellOff, Send, Building2, UsersRound, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, pushPermission, subscribeToPush, unsubscribeFromPush, sendTestPush, hasActiveSubscription } from "@/lib/push";
import AccountPanel from "@/components/AccountPanel";
import MembersPanel from "@/components/MembersPanel";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import MobileAppPanel from "@/components/MobileAppPanel";
import AppearancePanel from "@/components/AppearancePanel";
import CitySelector from "@/components/CitySelector";
import ManageSubscription from "@/components/ManageSubscription";
import FuelEnergyRatesPanel from "@/components/FuelEnergyRatesPanel";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { BookOpen, CreditCard, Zap as ZapIcon } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = !!(user && user.is_admin);
  const [sp, setSp] = useSearchParams();
  const initial = sp.get("tab") || "profile";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const t = sp.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const changeTab = (t) => {
    setTab(t);
    setSp({ tab: t }, { replace: true });
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="ft-overline mb-2">SETTINGS</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Control Room</h1>
        <p className="mt-3 text-[#A3A3A3]">
          {isAdmin ? "Manage your account, members, users, approvals and password resets." : "Manage your profile."}
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#262626] overflow-x-auto">
        <TabBtn active={tab === "profile"} onClick={() => changeTab("profile")} testid="settings-tab-profile">
          <Lock size={14} /> Profile
        </TabBtn>
        {isAdmin && (
          <TabBtn active={tab === "subscription"} onClick={() => changeTab("subscription")} testid="settings-tab-subscription">
            <CreditCard size={14} /> Manage Subscription
          </TabBtn>
        )}
        {isAdmin && (
          <TabBtn active={tab === "rates"} onClick={() => changeTab("rates")} testid="settings-tab-rates">
            <ZapIcon size={14} /> Fuel &amp; Energy
          </TabBtn>
        )}
        <TabBtn active={tab === "theme"} onClick={() => changeTab("theme")} testid="settings-tab-theme">
          <Palette size={14} /> Theme
        </TabBtn>
        {isAdmin && (
          <>
            <TabBtn active={tab === "account"} onClick={() => changeTab("account")} testid="settings-tab-account-info">
              <Building2 size={14} /> Account
            </TabBtn>
            <TabBtn active={tab === "members"} onClick={() => changeTab("members")} testid="settings-tab-members">
              <UsersRound size={14} /> Members
            </TabBtn>
            <TabBtn active={tab === "users"} onClick={() => changeTab("users")} testid="settings-tab-users">
              <Users size={14} /> Users
            </TabBtn>
            <TabBtn active={tab === "resets"} onClick={() => changeTab("resets")} testid="settings-tab-resets">
              <KeyRound size={14} /> Password Resets
            </TabBtn>
            <TabBtn active={tab === "prices"} onClick={() => changeTab("prices")} testid="settings-tab-prices">
              <Fuel size={14} /> Fuel Prices
            </TabBtn>
          </>
        )}
      </div>

      {tab === "profile" && <ProfilePanel />}
      {tab === "subscription" && isAdmin && <ManageSubscription />}
      {tab === "rates" && isAdmin && <FuelEnergyRatesPanel />}
      {tab === "theme" && <ThemeSwitcher />}
      {tab === "account" && isAdmin && <AccountPanel />}
      {tab === "members" && isAdmin && <MembersPanel />}
      {tab === "users" && isAdmin && <UsersPanel />}
      {tab === "resets" && isAdmin && <ResetsPanel />}
      {tab === "prices" && isAdmin && <FuelPricesPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 transition-colors duration-200 ${
        active
          ? "text-[var(--text-primary)] border-b-2 border-[#FF5A1F] -mb-px"
          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      }`}
    >
      {children}
    </button>
  );
}

function ProfilePanel() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords don't match");
    if (next.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="ft-card p-6 md:col-span-1">
        <p className="ft-overline mb-2">PROFILE</p>
        <p className="font-display text-2xl font-bold tracking-tight">{user?.name}</p>
        <p className="text-[#A3A3A3] font-mono text-sm mt-1 flex items-center gap-2"><Mail size={12} /> {user?.email}</p>
        <div className="mt-4 inline-flex items-center gap-2 border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F] px-2 py-1 text-xs font-bold uppercase tracking-wider">
          {user?.role}
        </div>
        <div className="mt-5 pt-5 border-t border-[#262626] space-y-3">
          <div>
            <label className="ft-overline block mb-1">Default city</label>
            <CitySelector value={user?.preferred_city} onChange={() => {}} testid="settings-city" />
          </div>
          <Link
            to="/tutorial"
            className="inline-flex items-center gap-2 text-sm text-[#FF5A1F] hover:text-white"
            data-testid="settings-tutorial-link"
          >
            <BookOpen size={14} /> Open help centre
          </Link>
        </div>
      </div>
      <form onSubmit={submit} className="ft-card p-6 md:col-span-2 space-y-4" data-testid="change-password-form">
        <p className="ft-overline">CHANGE PASSWORD</p>
        <FormInput label="Current Password" type="password" value={cur} onChange={setCur} testid="cp-current" required />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput label="New Password" type="password" value={next} onChange={setNext} testid="cp-new" required />
          <FormInput label="Confirm Password" type="password" value={confirm} onChange={setConfirm} testid="cp-confirm" required />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60"
          data-testid="cp-submit"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Update Password
        </button>
      </form>
      <div className="md:col-span-3">
        <PushNotificationsPanel />
      </div>
      <div className="md:col-span-3">
        <AppearancePanel />
      </div>
      <div className="md:col-span-3">
        <MobileAppPanel />
      </div>
    </div>
  );
}

function PushNotificationsPanel() {
  const [supported] = useState(isPushSupported());
  const [perm, setPerm] = useState(pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    hasActiveSubscription().then(setSubscribed);
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    const ok = await subscribeToPush();
    if (ok) { setSubscribed(true); setPerm("granted"); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true);
    const ok = await unsubscribeFromPush();
    if (ok) setSubscribed(false);
    setBusy(false);
  };
  const testIt = async () => {
    setBusy(true);
    await sendTestPush();
    setBusy(false);
  };

  return (
    <div className="ft-card p-6" data-testid="push-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="ft-overline">BROWSER NOTIFICATIONS</p>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Push Alerts</h3>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-xl">
            Get insurance renewal reminders (every 2 days, starting 30 days before expiry) and service alerts as
            native browser/mobile notifications — even when FuelTrack is closed.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs">
          <span className="font-mono text-[#737373]">Support: {supported ? "yes" : "no"}</span>
          <span className="font-mono text-[#737373]">Permission: {perm}</span>
          <span className={`font-mono ${subscribed ? "text-emerald-400" : "text-[#737373]"}`}>
            Subscription: {subscribed ? "active" : "inactive"}
          </span>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {!subscribed ? (
          <button
            onClick={enable}
            disabled={!supported || busy}
            className="bg-[#FF5A1F] text-black px-5 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors duration-200 disabled:opacity-60"
            data-testid="push-enable"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} Enable Notifications
          </button>
        ) : (
          <>
            <button
              onClick={testIt}
              disabled={busy}
              className="border border-[#262626] px-5 py-3 font-semibold hover:bg-[#1A1A1A] hover:border-[#FF5A1F]/50 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="push-test"
            >
              <Send size={14} /> Send Test
            </button>
            <button
              onClick={disable}
              disabled={busy}
              className="border border-[#262626] px-5 py-3 font-semibold text-[#A3A3A3] hover:text-red-400 hover:border-red-500/50 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="push-disable"
            >
              <BellOff size={14} /> Turn Off
            </button>
          </>
        )}
      </div>
      {perm === "denied" && (
        <p className="text-xs text-red-400 mt-3">
          Notifications are blocked in your browser. Enable them from the site settings (lock icon in the address bar) and reload.
        </p>
      )}
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [showPwFor, setShowPwFor] = useState(null); // user id
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (u, kind) => {
    try {
      await api.post(`/admin/users/${u.id}/${kind}`);
      toast.success(`${kind} · ${u.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This is permanent.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", addForm);
      toast.success("User created");
      setShowAdd(false);
      setAddForm({ name: "", email: "", password: "", role: "user" });
      load();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  const setPw = async (u) => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 chars");
    try {
      await api.post(`/admin/users/${u.id}/password`, { new_password: newPw });
      toast.success(`Password updated for ${u.email}`);
      setShowPwFor(null);
      setNewPw("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badge = (status) => {
    const map = {
      pending: "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]",
      active: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
      suspended: "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]",
    };
    return <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wider border ${map[status] || ""}`}>{status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="ft-overline">USERS</p>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">All accounts</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="border border-[#262626] px-4 py-2 hover:bg-[#1A1A1A] transition-colors inline-flex items-center gap-2" data-testid="users-refresh"><RefreshCw size={14} /> Refresh</button>
          <button
            onClick={() => setShowAdd((s) => !s)}
            className="bg-[#FF5A1F] text-black px-4 py-2 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors"
            data-testid="add-user-toggle"
          >
            <UserPlus size={14} /> {showAdd ? "Cancel" : "Add User"}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addUser} className="ft-card p-6 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="add-user-form">
          <FormInput label="Name" value={addForm.name} onChange={(v) => setAddForm({ ...addForm, name: v })} testid="au-name" required />
          <FormInput label="Email" type="email" value={addForm.email} onChange={(v) => setAddForm({ ...addForm, email: v })} testid="au-email" required />
          <FormInput label="Password" type="password" value={addForm.password} onChange={(v) => setAddForm({ ...addForm, password: v })} testid="au-password" required />
          <div>
            <label className="ft-overline block mb-2">Role</label>
            <select
              value={addForm.role}
              onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors"
              data-testid="au-role"
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button type="submit" disabled={saving} className="bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] disabled:opacity-60" data-testid="au-submit">
              {saving && <Loader2 size={16} className="animate-spin" />} Create
            </button>
          </div>
        </form>
      )}

      <div className="ft-card p-0 overflow-auto">
        {loading ? <div className="p-12 text-center text-[#737373]">Loading…</div> : (
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr>
                {["Name / Email", "Role", "Status", "Actions"].map((h) => (
                  <th key={h} className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="py-4 px-4">
                    <div className="text-white font-bold">{u.name}</div>
                    <div className="text-[#A3A3A3] text-xs">{u.email}</div>
                  </td>
                  <td className="py-4 px-4">{u.role}</td>
                  <td className="py-4 px-4">{badge(u.status || "active")}</td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-2">
                      {u.status === "pending" && (
                        <button onClick={() => act(u, "approve")} className="border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-black px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`approve-user-${u.id}`}>
                          <Check size={14} /> Approve
                        </button>
                      )}
                      {u.status === "active" && u.role !== "admin" && (
                        <button onClick={() => act(u, "suspend")} className="border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`suspend-user-${u.id}`}>
                          <ShieldOff size={14} /> Suspend
                        </button>
                      )}
                      {u.status === "suspended" && (
                        <button onClick={() => act(u, "activate")} className="border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-black px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`activate-user-${u.id}`}>
                          <Shield size={14} /> Activate
                        </button>
                      )}
                      <button onClick={() => { setShowPwFor(u.id); setNewPw(""); }} className="border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#FF5A1F] px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`setpw-user-${u.id}`}>
                        <KeyRound size={14} /> Set Password
                      </button>
                      {u.role !== "admin" && (
                        <button onClick={() => remove(u)} className="border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                    </div>
                    {showPwFor === u.id && (
                      <div className="mt-3 flex gap-2 items-center">
                        <input
                          type="password"
                          value={newPw}
                          onChange={(e) => setNewPw(e.target.value)}
                          placeholder="new password"
                          className="bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF5A1F]"
                          data-testid={`setpw-input-${u.id}`}
                        />
                        <button onClick={() => setPw(u)} className="bg-[#FF5A1F] text-black px-3 py-2 text-xs font-bold" data-testid={`setpw-save-${u.id}`}>Save</button>
                        <button onClick={() => { setShowPwFor(null); setNewPw(""); }} className="border border-[#262626] text-[#A3A3A3] px-2 py-2"><X size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ResetsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfillFor, setFulfillFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/password-resets");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fulfill = async (item) => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 chars");
    try {
      await api.post(`/admin/password-resets/${item.id}/fulfill`, { new_password: newPw });
      toast.success(`Password set for ${item.email}`);
      setFulfillFor(null); setNewPw("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dismiss = async (item) => {
    try {
      await api.post(`/admin/password-resets/${item.id}/dismiss`);
      toast.success("Dismissed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badge = (status) => {
    const map = {
      pending: "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]",
      fulfilled: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
      dismissed: "border-[#737373]/40 bg-[#737373]/10 text-[#737373]",
    };
    return <span className={`px-2 py-1 text-xs font-bold uppercase tracking-wider border ${map[status] || ""}`}>{status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="ft-overline">RESET REQUESTS</p>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Forgot-password queue</h3>
        </div>
        <button onClick={load} className="border border-[#262626] px-4 py-2 hover:bg-[#1A1A1A] transition-colors inline-flex items-center gap-2" data-testid="resets-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="ft-card p-0 overflow-auto">
        {loading ? <div className="p-12 text-center text-[#737373]">Loading…</div>
        : items.length === 0 ? <div className="p-12 text-center text-[#A3A3A3]">No reset requests.</div>
        : (
          <table className="w-full" data-testid="resets-table">
            <thead>
              <tr>
                {["Email", "Reason", "Requested", "Status", "Actions"].map((h) => (
                  <th key={h} className="text-left py-4 px-4 text-xs uppercase tracking-wider font-bold text-[#737373] border-b border-[#262626]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors" data-testid={`reset-row-${it.id}`}>
                  <td className="py-4 px-4 text-white">{it.email}</td>
                  <td className="py-4 px-4 text-[#A3A3A3] max-w-md truncate">{it.reason || "—"}</td>
                  <td className="py-4 px-4 text-[#A3A3A3]">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-4 px-4">{badge(it.status)}</td>
                  <td className="py-4 px-4">
                    {it.status === "pending" ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                          {fulfillFor === it.id ? (
                            <>
                              <input
                                type="password"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                placeholder="new password"
                                className="bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-white text-xs focus:outline-none focus:border-[#FF5A1F]"
                                data-testid={`reset-pw-input-${it.id}`}
                              />
                              <button onClick={() => fulfill(it)} className="bg-[#10B981] text-black px-3 py-2 text-xs font-bold" data-testid={`reset-fulfill-save-${it.id}`}>Save</button>
                              <button onClick={() => { setFulfillFor(null); setNewPw(""); }} className="border border-[#262626] text-[#A3A3A3] px-2 py-2"><X size={14} /></button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => { setFulfillFor(it.id); setNewPw(""); }} className="border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-black px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`reset-fulfill-${it.id}`}>
                                <KeyRound size={14} /> Set new password
                              </button>
                              <button onClick={() => dismiss(it)} className="border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444] hover:text-white px-3 py-1.5 inline-flex items-center gap-1 transition-colors" data-testid={`reset-dismiss-${it.id}`}>
                                <X size={14} /> Dismiss
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-[#737373] text-xs">Handled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FuelPricesPanel() {
  const { user } = useAuth();
  const preferredCity = user?.preferred_city || "Ahmedabad";
  const [prices, setPrices] = useState({});
  const [city, setCity] = useState(preferredCity);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [syncSource, setSyncSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Load prices for a specific city — prefers the modern per-city endpoint
  // (respects tenant overrides + sync-cities scrapes) and falls back to the
  // legacy /settings/fuel-prices doc so the last-sync metadata is still
  // available for the sync-now button.
  const load = useCallback(async (target) => {
    const cityForLoad = target || preferredCity;
    setLoading(true);
    try {
      const [modern, legacy] = await Promise.all([
        api.get(`/rates/fuel?city=${encodeURIComponent(cityForLoad)}`),
        api.get("/settings/fuel-prices"),
      ]);
      const merged = { ...(modern.data.rates || {}) };
      // /rates/fuel doesn't include EV; carry the legacy EV rate through.
      if (legacy.data.prices?.EV != null && merged.EV == null) {
        merged.EV = legacy.data.prices.EV;
      }
      setPrices(merged);
      setCity(cityForLoad);
      setUpdatedAt(modern.data.effective_date || legacy.data.updated_at);
      setLastSyncAt(legacy.data.last_sync_at);
      setLastSyncStatus(legacy.data.last_sync_status);
      setSyncSource(modern.data.source || legacy.data.sync_source);
    } finally { setLoading(false); }
  }, [preferredCity]);

  // Refetch every time the header CitySelector changes user.preferred_city
  useEffect(() => { load(preferredCity); }, [preferredCity, load]);

  const setPrice = (k, v) => setPrices((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(prices)) {
        const n = Number(v);
        if (!isNaN(n)) payload[k] = n;
      }
      // Persist per-city override so this admin edit stays scoped to the
      // currently selected city (and stops overwriting Ahmedabad every time).
      const overrideBody = { city };
      for (const k of ["petrol", "diesel", "cng"]) {
        const cap = k.charAt(0).toUpperCase() + k.slice(1);
        if (payload[cap] != null) overrideBody[k] = payload[cap];
      }
      await api.put("/rates/fuel/override", overrideBody);
      // Also update the legacy settings doc so NewRefuel auto-fill keeps working.
      await api.put("/settings/fuel-prices", { prices: payload, city });
      toast.success(`Fuel prices updated for ${city}`);
      load(city);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      // First point the legacy settings doc at the currently selected city
      // so the sync-now endpoint scrapes for the RIGHT city.
      await api.put("/settings/fuel-prices", { prices: prices || {}, city });
      const { data } = await api.post("/settings/fuel-prices/sync");
      if (data.ok) toast.success(`Synced ${data.synced?.length || 0} fuel(s) from live feed`);
      else toast.error("Sync failed — feed unreachable");
      load(city);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSyncing(false); }
  };

  if (loading) return <div className="text-[#737373]">Loading…</div>;

  const keys = ["Petrol", "Diesel", "CNG", "EV"];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="ft-overline">PREVAILING RATES</p>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">Fuel prices auto-fill</h3>
          <p className="text-[#A3A3A3] text-sm mt-1">
            Used to pre-fill the rate on New Refuel when the receipt doesn't tell us. Synced from goodreturns.in every morning.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={syncNow}
            disabled={syncing}
            className="bg-[#3B82F6] text-white px-4 py-2 font-bold inline-flex items-center gap-2 hover:bg-[#2563EB] transition-colors disabled:opacity-60"
            data-testid="fp-sync-now"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync Now
          </button>
          {lastSyncAt && (
            <div className="text-xs text-[#737373] font-mono text-right" data-testid="fp-sync-status">
              Last synced {lastSyncAt.slice(0, 16).replace("T", " ")}
              {lastSyncStatus && <div>· {lastSyncStatus}{syncSource ? ` · ${syncSource}` : ""}</div>}
            </div>
          )}
          {updatedAt && (
            <div className="text-xs text-[#737373] font-mono">Edited {updatedAt.slice(0, 16).replace("T", " ")}</div>
          )}
        </div>
      </div>

      <div className="ft-card p-6 space-y-5" data-testid="fuel-prices-panel">
        <div>
          <label className="ft-overline block mb-2">City</label>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] font-mono text-[var(--text-primary)]"
              data-testid="fp-city"
              aria-label={`Fuel prices for ${city}`}
            >
              <Fuel size={14} className="text-[#FF5A1F]" /> {city}
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              Change this via the <span className="font-semibold text-[#FF5A1F]">city selector in the header</span> — prices below reload automatically.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {keys.map((k) => (
            <div key={k}>
              <label className="ft-overline block mb-2 flex items-center gap-2">
                <Fuel size={12} className="text-[#FF5A1F]" /> {k}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] font-mono">₹</span>
                <input
                  type="number"
                  step="0.01"
                  value={prices[k] ?? ""}
                  onChange={(e) => setPrice(k, e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#262626] pl-8 pr-4 py-3 font-mono text-white focus:outline-none focus:border-[#FF5A1F] transition-colors"
                  data-testid={`fp-${k.toLowerCase()}`}
                />
              </div>
              <p className="text-xs text-[#737373] mt-1">per litre</p>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-2 border-t border-[#262626]">
          <button
            onClick={save}
            disabled={saving}
            className="bg-[#FF5A1F] text-black px-6 py-3 font-bold inline-flex items-center gap-2 hover:bg-[#E04F1A] transition-colors disabled:opacity-60"
            data-testid="fp-save"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Fuel size={16} />} Save Prices
          </button>
        </div>
      </div>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", required, placeholder, testid }) {  return (
    <div>
      <label className="ft-overline block mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[var(--input-bg,var(--bg-elevated))] border border-[var(--input-border,var(--border-line))] px-4 py-3 font-mono text-[var(--input-fg,var(--text-primary))] focus:outline-none focus:border-[#FF5A1F] transition-colors duration-200"
        data-testid={testid}
      />
    </div>
  );
}
